import React, { createRef, ReactNode, useEffect } from 'react';
import { Unity, useUnityContext } from 'react-unity-webgl';
import { MapTrafficVolume } from 'app/model/CORE';
import { Spin } from 'antd';
// NOTE(bjarne): These .js files work in dev and build with ?inline, but that inlines the file meaning no caching.
import loaderUrlProd from 'static-assets/WebGLBuild/nmui4d-xr-frontend_UNCOMPRESSED.loader.js?no-inline';
import frameworkUrlProd from 'static-assets/WebGLBuild/nmui4d-xr-frontend_UNCOMPRESSED.framework.js?no-inline';
import loaderUrlDev from 'static-assets/WebGLBuild/nmui4d-xr-frontend_UNCOMPRESSED.loader.js?inline';
import frameworkUrlDev from 'static-assets/WebGLBuild/nmui4d-xr-frontend_UNCOMPRESSED.framework.js?inline';
import dataUrl from 'static-assets/WebGLBuild/nmui4d-xr-frontend_UNCOMPRESSED.data';
import codeUrl from 'static-assets/WebGLBuild/nmui4d-xr-frontend_UNCOMPRESSED.wasm';

import { UnityCanvasSendMessageRequestForwarder } from './UnityCanvasSendMessageRequestForwarder';
import { UnityCanvasEventNotificationForwarder } from './UnityCanvasEventNotificationForwarder';

type TProps = {
    customControls?: ReactNode;
    selectedMapTvs?: MapTrafficVolume[];
    setIsLoaded: React.Dispatch<React.SetStateAction<boolean>>;
};

export const UnityCanvas = (props: React.PropsWithChildren<TProps>) => {
    const canvasRef = createRef<HTMLCanvasElement>();
    const canvasContainerRef = createRef<HTMLDivElement>();

    const loaderUrl = import.meta.env.DEV ? loaderUrlDev : loaderUrlProd;
    const frameworkUrl = import.meta.env.DEV ? frameworkUrlDev : frameworkUrlProd;

    // TODO(bjarne): Do we need to be able to set this header? 'Content-Encoding: br'
    // https://docs.unity3d.com/Manual/webgl-deploying.html
    const { unityProvider, isLoaded, sendMessage, addEventListener, removeEventListener } = useUnityContext({
        loaderUrl: loaderUrl,
        dataUrl: dataUrl,
        frameworkUrl: frameworkUrl,
        codeUrl: codeUrl,
        // NOTE(bjarne): I don't think we can include a folder using Vite's static asset system, so we just put
        // this in the public folder. Since these files don't really change it should be fine.
        streamingAssetsUrl: 'WebGLBuild/StreamingAssets',
    });

    useEffect(() => {
        const onResize = () => {
            if (!canvasRef.current) {
                return;
            }

            /*
                Setting to a low value first gives the parent container a chance to drop its height again, after which
                it will fit itself to 100% of the higher-up flex container.

                This fixes the clientHeight below from becoming slightly larger than the flexbox wants or parent
                container has, making both parents size to fit, making the entire container a few pixels larger than
                what should be left, in turn resulting in ever-increasing sizes of the canvas and thus containers.
             */
            canvasRef.current.width = 1;
            canvasRef.current.height = 1;

            /*
                Subtract a single pixel because on some heights you still see an unscrollable scrollbar and 'scroll:
                overflow' triggers despite the canvas size exactly matching the parent. This is likely due to the rest
                of the content in the flexbox having fractional sizes, which this works around.
            */
            canvasRef.current.width = canvasContainerRef.current?.clientWidth;
            canvasRef.current.height = canvasContainerRef.current?.clientHeight - 1;
        };

        onResize();

        window.addEventListener('resize', onResize);

        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, [canvasRef, canvasContainerRef]);

    useEffect(() => {
        props.setIsLoaded(isLoaded);
    }, [isLoaded]);

    return (
        <div
            style={{
                flex: '1 1 auto',
                flexDirection: 'column',
                display: 'flex',
            }}>
            <Spin wrapperClassName="panel-spinner" spinning={!isLoaded} size={'large'}>
                <div
                    ref={canvasContainerRef}
                    style={{
                        flex: '1 1 auto',
                        padding: '0',
                        margin: '0',
                        lineHeight: '0',
                    }}>
                    <Unity unityProvider={unityProvider} ref={canvasRef}></Unity>
                </div>
            </Spin>
            {props.customControls}
            {props.children}
            <UnityCanvasSendMessageRequestForwarder
                isLoaded={isLoaded}
                sendMessage={sendMessage}></UnityCanvasSendMessageRequestForwarder>
            <UnityCanvasEventNotificationForwarder
                addEventListener={addEventListener}
                removeEventListener={removeEventListener}></UnityCanvasEventNotificationForwarder>
        </div>
    );
};
